/**
 * @file JS - Controller - PointVectorLayerController.js
 * @author Adam Lemire
 * @since 2020/07/22
 * @version 1.0
 * @summary Dernières modifications : 2021/12/15
 */

import { LayerProperties } from '../../../helpers/Selectors.js';
import { ClassificationType, ColorHexList, ColorPalettes, GradationType, StyleTypes } from '../../../helpers/Constants.js';
import { getRandomRGBAColor } from '../../../helpers/GlobalFunctions.js';
import VectorLayer from '../../../classes/Layer/VectorLayer.js'; // eslint-disable-line no-unused-vars
import VectorLayerStyle from '../../../classes/Layer/VectorLayerStyle.js'; // eslint-disable-line no-unused-vars

/** Controller devant être adapté par VectorLayerAdapter */
export default class PointVectorLayerController {
  static createSymbolPreviewDiv () {
    const marginTop = $(LayerProperties.LAYER_SYMBOL_SIZE).val() < 13 ? 24 - $(LayerProperties.LAYER_SYMBOL_SIZE).val() + 'px' : '10px';
    const symbol = '<div class="symbol-preview__content" style="' +
      'width:' + $(LayerProperties.LAYER_SYMBOL_SIZE).val() * 2 + 'px; ' +
      'height:' + $(LayerProperties.LAYER_SYMBOL_SIZE).val() * 2 + 'px; ' +
      'border:' + $(LayerProperties.LAYER_STROKE_WIDTH).val() + 'px solid ' + $(LayerProperties.BUTTON.STROKE_COLOR).css('background-color') + '; ' +
      'WebkitBorderRadius:' + $(LayerProperties.LAYER_SYMBOL_SIZE).val() + $(LayerProperties.LAYER_STROKE_WIDTH).val() + 'px; ' +
      'MozBorderRadius:' + $(LayerProperties.LAYER_SYMBOL_SIZE).val() + $(LayerProperties.LAYER_STROKE_WIDTH).val() + 'px; ' +
      'border-radius:' + $(LayerProperties.LAYER_SYMBOL_SIZE).val() + $(LayerProperties.LAYER_STROKE_WIDTH).val() + 'px; ' +
      'background:' + $(LayerProperties.FILL.COLOR).css('background-color') + ';' +
      'margin: ' + marginTop + ' 0px 0px 10px; ' +
      'top: 50%; ' +
      '"></div>';

    return symbol;
  }

  static creerDivApercuSymboleAutres () {
    const marginTop = $(LayerProperties.SYMBOLE_AUTRES_TAILLE).val() < 13 ? 24 - $(LayerProperties.SYMBOLE_AUTRES_TAILLE).val() + 'px' : '10px';
    const symbol = '<div class="symbol-preview__content" style="' +
      'width:' + $(LayerProperties.SYMBOLE_AUTRES_TAILLE).val() * 2 + 'px; ' +
      'height:' + $(LayerProperties.SYMBOLE_AUTRES_TAILLE).val() * 2 + 'px; ' +
      'border:' + $(LayerProperties.SYMBOLE_AUTRES_CONTOUR_EPAISSEUR).val() + 'px solid ' + $(LayerProperties.SYMBOLE_AUTRES_CONTOUR_COULEUR).css('background-color') + '; ' +
      'WebkitBorderRadius:' + $(LayerProperties.SYMBOLE_AUTRES_TAILLE).val() + $(LayerProperties.SYMBOLE_AUTRES_CONTOUR_EPAISSEUR).val() + 'px; ' +
      'MozBorderRadius:' + $(LayerProperties.SYMBOLE_AUTRES_TAILLE).val() + $(LayerProperties.SYMBOLE_AUTRES_CONTOUR_EPAISSEUR).val() + 'px; ' +
      'border-radius:' + $(LayerProperties.SYMBOLE_AUTRES_TAILLE).val() + $(LayerProperties.SYMBOLE_AUTRES_CONTOUR_EPAISSEUR).val() + 'px; ' +
      'background:' + $(LayerProperties.SYMBOLE_AUTRES_COULEUR).css('background-color') + ';' +
      'margin: ' + marginTop + ' 0px 0px 10px; ' +
      'top: 50%; ' +
      '"></div>';

    return symbol;
  }

  static showFeatureStyleProperties () {
    $(LayerProperties.FILL.TITLE_LABEL).show();
    $(LayerProperties.FILL.COLOR_LABEL).show();
    $(LayerProperties.FILL.COLOR).show();
    $(LayerProperties.LAYER_STROKE_COLOR).show();
    $(LayerProperties.LAYER_STROKE_WIDTH).show();
    $(LayerProperties.LABEL_SYMBOL_SIZE).removeClass('hidden');
    $(LayerProperties.LAYER_SYMBOL_SIZE).removeClass('hidden');
    $(LayerProperties.SYMBOLE_AUTRES_TAILLE).show();
    $(LayerProperties.SYMBOLE_AUTRES_TAILLE_ETIQUETTE).show();
  }

  /*
   * Groupes
   */

  /**
   * @param { Map < number, GroupStyle > } symbolStyles
   */
  static createGroupsPropertiesRows (symbolStyles) {
    $('.advanced-preview__row').remove();

    symbolStyles.forEach(function (symbolStyle, index) {
      const row = `
      <tr class="advanced-preview__row" data-group="` + symbolStyle.groupValue + `">
        <td class="advanced-preview__symbol--` + symbolStyle.id + '" style="position:relative; height:30px; width:30px;"><div class="symbol-preview__content symbol-preview__content--point symbol-preview__content--' + symbolStyle.id + `">
        </div></td>
        <td class="advanced-preview__group">` + symbolStyle.groupValueLabel + `</td>
        <td class="advanced-preview__label"><input type="text" id="advanced-preview__text--` + symbolStyle.id + '" class="advanced-preview__text" value="' + symbolStyle.groupLabel + `"></td>
        <td class="advanced-preview__counter type-graduated">` + symbolStyle.count + `</td>
      </tr>`;
      $(LayerProperties.ADVANCED_PREVIEW.CONTAINER).append(row);
      PointVectorLayerController.createGroupSymbolPreviewDiv(symbolStyle);
    });
  }

  /**
   * @param { VectorLayer } layer
   * @returns { Map < number, GroupStyle > }
   */
  static getNewSymbolStyles (layer) {
    const symbolStyles = $(LayerProperties.SYMBOL_STYLE_TYPE).val() === StyleTypes.CATEGORIZED
      ? PointVectorLayerController.getNewCategorizedSymboleStyles(layer)
      : PointVectorLayerController.getNewGraduatedSymbolStyles(layer);
    return symbolStyles;
  }

  /**
   * @param { VectorLayer } layer
   * @returns { Map < number, GroupStyle > }
   */
  static getNewCategorizedSymboleStyles (layer) {
    const groups = layer.obtenirValeursDistinctes($(LayerProperties.GROUP_VALUE_SELECTOR).val());
    const radius = 6;
    const strokeWidth = groups.length < 31 ? 1 : 0;
    const symbolStyles = new Map();

    groups.forEach(function (group, index) {
      const randomColor = index < 30 ? 0 : getRandomRGBAColor();
      const backgroundColor = index < 10
        ? ColorHexList.BRIGHT[index]
        : index < 20
          ? ColorHexList.LIGHT[index - 10]
          : index < 30
            ? ColorHexList.DARK[index - 20]
            : randomColor;
      const strokeColor = index < 10
        ? ColorHexList.DARK[index]
        : index < 20
          ? ColorHexList.BRIGHT[index - 10]
          : index < 30
            ? ColorHexList.LIGHT[index - 20]
            : randomColor;
      const symbolStyle = {
        id: index,
        fillColor: backgroundColor,
        strokeColor: strokeColor,
        strokeWidth: strokeWidth,
        groupLabel: group,
        groupValueLabel: group,
        groupValue: group,
        radius: radius
      };
      symbolStyles.set(group, symbolStyle);
    });
    return symbolStyles;
  }

  /**
   * @param { object } classification
   * @returns { Map < number, GroupStyle > }
   */
  static getNewGraduatedSymbolStyles (classification) {
    const strokeWidth = 1;
    const symbolStyles = new Map();
    const precision = $(LayerProperties.PRECISION_SELECTOR).val();
    const counter = classification.groups.counter;

    try {
      if (classification.type === ClassificationType.ATTRIBUTE) {
        const groups = classification.groups.bounds || [0];
        groups.sort(function (a, b) { return a - b; });

        const max = classification.groups.max();

        groups.forEach(function (group, index) {
          let backgroundColor;
          let radius;
          const groupNumber = isNaN(group) ? 0 : group;
          const colorPalette = ColorPalettes[$(LayerProperties.COLOR_GRADIENT_SELECTOR).val()];
          const strokeColor = 'black';

          if ($('.gradation-type__input:checked').val() === GradationType.COLOR) {
            backgroundColor = colorPalette(1 - group / (max));
            radius = 6;
          } else {
            backgroundColor = colorPalette(0);
            radius = Math.round((groups.length < 12 ? groups.length : 12) * ((groupNumber) / (max)));
          }

          /** @type { GroupStyle } */
          const symbolStyle = {
            id: index,
            count: counter[index],
            fillColor: backgroundColor,
            strokeColor: strokeColor,
            strokeWidth: strokeWidth,
            groupLabel: group.toFixed(precision),
            groupValueLabel: group.toFixed(precision),
            groupValue: group,
            radius: radius
          };
          symbolStyles.set(group, symbolStyle);
        });
      } else {
        const ranges = classification.groups.ranges;

        ranges.forEach(function (groupLabel, index) {
          let backgroundColor;
          let radius;
          const groupNumber = index;
          const colorPalette = ColorPalettes[$(LayerProperties.COLOR_GRADIENT_SELECTOR).val()];
          const strokeColor = 'black';

          if (classification.type === ClassificationType.STD_DEVIATION) {
            backgroundColor = ColorHexList.COLD_HOT[index];
            radius = 6;
          } else {
            if ($('.gradation-type__input:checked').val() === GradationType.COLOR) {
              backgroundColor = colorPalette(1 - index / (ranges.length - 1));
              radius = 6;
            } else {
              backgroundColor = colorPalette(0);
              radius = groupNumber + 1;
            }
          }

          /** @type { GroupStyle } */
          const symbolStyle = {
            id: index,
            count: counter[index],
            fillColor: backgroundColor,
            strokeColor: strokeColor,
            strokeWidth: strokeWidth,
            groupLabel: groupLabel,
            groupValueLabel: groupLabel,
            groupValue: groupNumber,
            radius: radius
          };
          symbolStyles.set(groupNumber, symbolStyle); // index, symbolStyle
        });
      }
      return symbolStyles;
    } catch (err) {
      console.log(err.message);
    }
  }

  /**
   * @param { GroupStyle } groupStyle
   */
  static createGroupSymbolPreviewDiv (groupStyle) {
    const div = '.symbol-preview__content--' + groupStyle.id;

    const point = '<div style="' +
      'width:' + groupStyle.radius * 2 + 'px; ' +
      'height:' + groupStyle.radius * 2 + 'px; ' +
      'border-width:' + groupStyle.strokeWidth + 'px; ' +
      'border-style: solid; ' +
      'border-color:' + groupStyle.strokeColor + '; ' +
      'WebkitBorderRadius:' + groupStyle.radius + groupStyle.strokeWidth + 'px; ' +
      'MozBorderRadius:' + groupStyle.radius + groupStyle.strokeWidth + 'px; ' +
      'border-radius:' + groupStyle.radius + groupStyle.strokeWidth + 'px; ' +
      'background: ' + groupStyle.fillColor + ';' +
      'margin: 0px auto; ' +
      'top: 50%; ' +
      '"></div>';

    $(div).append(point);
  }

  /**
   * @param { GroupStyle } advancedStyleObject
   */
  static updatePropertyEditorValues (advancedStyleObject) {
    $(LayerProperties.FILL.COLOR).css('background-color', advancedStyleObject.fillColor);
    $(LayerProperties.BUTTON.STROKE_COLOR).css('background-color', advancedStyleObject.strokeColor);
    $(LayerProperties.LAYER_STROKE_WIDTH).val(advancedStyleObject.strokeWidth);
    $(LayerProperties.LAYER_SYMBOL_SIZE).val(advancedStyleObject.radius);
    $(LayerProperties.SYMBOL_STYLE_GROUP_LABEL).html(advancedStyleObject.groupLabel);
    $(LayerProperties.SYMBOL_STYLE_GROUP_LABEL).data('group', advancedStyleObject.groupValue); // advancedStyleObject.id
  }

  /**
   * @param { VectorLayerStyle } vectorLayerStyle
   */
  static updatePropertyEditorGroupSymbol (vectorLayerStyle) {
    if ($(LayerProperties.SYMBOL_STYLE_TYPE).val() !== StyleTypes.SINGLE) {
      const group = $(LayerProperties.SYMBOL_STYLE_GROUP_LABEL).data('group');
      const groupId = vectorLayerStyle.symbolStyles.get(group).id;

      const div = '.symbol-preview__content--' + groupId + ' div';

      $(div).css('border', $(LayerProperties.LAYER_STROKE_WIDTH).val() + 'px solid ' + $(LayerProperties.BUTTON.STROKE_COLOR).css('background-color'));
      $(div).css('background-color', $(LayerProperties.FILL.COLOR).css('background-color'));
      $(div).css('height', $(LayerProperties.LAYER_SYMBOL_SIZE).val() * 2);
      $(div).css('width', $(LayerProperties.LAYER_SYMBOL_SIZE).val() * 2);

      vectorLayerStyle.symbolStyles.get(group).fillColor = $(LayerProperties.FILL.COLOR).css('background-color');
      vectorLayerStyle.symbolStyles.get(group).strokeColor = $(LayerProperties.BUTTON.STROKE_COLOR).css('background-color');
      vectorLayerStyle.symbolStyles.get(group).strokeWidth = $(LayerProperties.LAYER_STROKE_WIDTH).val();
      vectorLayerStyle.symbolStyles.get(group).radius = $(LayerProperties.LAYER_SYMBOL_SIZE).val();
    }
  }

  /*
   * Légende
   */

  /**
   * @param { object } symbolStyle
   * @returns { string }
   */
  static createSingleLegendSymbolDiv (symbolStyle) {
    const circle = '<div class="legend__single-symbol legend__single-symbol--point"><div style="' +
      'width:' + symbolStyle.radius * 2 + 'px; ' +
      'height:' + symbolStyle.radius * 2 + 'px; ' +
      'border:' + symbolStyle.strokeWidth + 'px solid ' + symbolStyle.strokeColor + '; ' +
      'WebkitBorderRadius:' + symbolStyle.radius + symbolStyle.strokeWidth + 'px; ' +
      'MozBorderRadius:' + symbolStyle.radius + symbolStyle.strokeWidth + 'px; ' +
      'border-radius:' + symbolStyle.radius + symbolStyle.strokeWidth + 'px; ' +
      'background:' + symbolStyle.fillColor + ';' +
      'margin: 0px 0px 0px 0px; ' +
      'top: 50%; ' +
      '"></div>';

    return circle;
  }

  /**
   * @param { Map } symbolStyles
   * @returns {string}
   */
  static createMultiLegendSymbolsDiv (symbolStyles) {
    let divs = '<table class="legend__multi-styles">';
    symbolStyles.forEach(function (value, key) {
      const div = '<tr class="legend__group-container"><td class="legend__group-symbol legend__group-symbol--point"><div style="' +
      'width:' + value.radius * 2 + 'px; ' +
      'height:' + value.radius * 2 + 'px; ' +
      'border:' + value.strokeWidth + 'px solid ' + value.strokeColor + '; ' +
      'WebkitBorderRadius:' + value.radius + value.strokeWidth + 'px; ' +
      'MozBorderRadius:' + value.radius + value.strokeWidth + 'px; ' +
      'border-radius:' + value.radius + value.strokeWidth + 'px; ' +
      'background:' + value.fillColor + ';' +
      'margin: 0px auto; ' +
      'top: 50%; ' +
      '"></div></td><td class="legend__group-label">' + value.groupLabel + '</td></tr>';
      divs += div;
    });
    divs += '</table>';
    return divs;
  }
}
